// frontend/src/components/homepage/ScannedTray.js
//
// "פוסטים שנסרקו מאז שנכנסת לאתר" and a number that climbs while you read. It
// is the page's only live claim, and it earns the hero: the graph behind it is
// an illustration, this is a count.
//
// THE COUNT IS DERIVED FROM ELAPSED TIME, not incremented per tick. A visitor
// who switches tabs for two minutes should come back to a number that reflects
// two minutes - "since you arrived" is a wall-clock claim, and a paused
// increment would quietly under-report it. So the interval is cleared while the
// tab is hidden (there is nothing to burn CPU for) and the value is recomputed
// from `Date.now()` on the way back.
//
// IT FREEZES WITH MOTION OFF (owner decision, 2026-09-03). An earlier reading
// kept it counting on the grounds that the number is content rather than
// motion; the owner's call is the opposite and it is the right one - a digit
// that rewrites itself twice a second IS movement, whatever it means, and it
// sits in the corner of the eye of exactly the visitor who asked the page to
// hold still. So motion-off clears the interval and holds the last value shown,
// alongside the ping ring it already dropped.
//
// Resuming re-derives from the wall clock rather than continuing from the held
// value, which is the SAME rule the hidden-tab path already follows: whenever
// the number is being updated at all it equals `elapsed / TICK_MS`, and
// motion-off simply stops updating it. A visitor who leaves the toggle on for
// the whole visit sees the value it was frozen at (0, if it was never on).
//
// THE DOCK. Once the hero scrolls past, the tray flies up into the navbar and
// becomes a quiet nav item, so the live number survives the whole page. Three
// things make that harder than it looks:
//
//   1. The stage has `perspective`, which makes it a containing block for
//      `position: fixed`. A fixed tray inside it would be positioned against the
//      STAGE, not the viewport, and would scroll away with it. So the docked
//      tray is PORTALLED to document.body - the React-legal form of the
//      prototype's `document.body.appendChild(tray)`.
//   2. A portal switch remounts the element, so the FLIP has to bracket the
//      state change: measure `first` before, `last` in a layout effect after.
//   3. The navbar has to make room, or the tray lands on top of the wordmark.
//      HomeNav.js carries one inert zero-width slot; this component measures it
//      and grows it. Nothing about the navbar's own layout changes when the slot
//      is at zero.
//
// AND THE PILL MOVES AFTER THE DOCK. Growing the slot is what tells HomeNav
// (a MutationObserver on the slot) to add `hl-nav-has-dock`, which widens the
// pill 1120 -> 1380px a render LATER - so the slot's position changes after
// the one placement made at dock time. With motion the follow loop rides that
// out; with motion off nothing moved the tray again until the next scroll, and
// after a jump (a nav-link click) it sat ~130px off its slot over the links
// (review FR2 #1). While docked a ResizeObserver therefore watches the pill
// and the tray itself and re-places the tray in the same frame - in both
// motion modes, and without animating anything: it is a placement, not a loop.
// Watching the tray also re-sizes the slot when the count gains a glyph
// (999 -> 1,000), which used to run the number into the CTA.
//
// TWO DOCKED SHAPES (owner round 4). The pill's own layout changes at 1080px -
// links out, burger in, wordmark and burger at the two ends - and the docked
// tray changes with it at the same width:
//
//   * above 1080: the full tray, icon + caption + count, on a hairline divider.
//   * at or below: a COMPACT CHIP, icon + count only, 28px tall and borderless.
//     The caption is clipped rather than dropped, so it is still read aloud.
//
// The chip lands in the SAME `#lf-nav-dock` slot, which sits immediately after
// the wordmark - so it docks where the owner asked for it, beside the wordmark,
// while the register CTA and the burger keep the pill's trailing end. The
// phone row's arithmetic, per width and per count, is in homeNav.css's
// phone-pill block.
//
// It is the same FLIP, the same hysteresis and the same portal as the desktop
// dock - only the rendered shape differs - so there is one dock to reason
// about, not two. Under the motion switch it simply appears: the FLIP is
// already skipped when `motionOff`, and the slot's width transition is zeroed
// by homepage.css's motion-off block.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** One scanned post every 850ms - the prototype's cadence. */
export const TICK_MS = 850;
/** The navbar slot HomeNav.js renders. */
export const DOCK_SLOT_ID = "lf-nav-dock";
/**
 * At or below this the pill is in its phone layout (wordmark + burger, the
 * links in a sheet) and the tray docks as a COMPACT CHIP - icon and number
 * only, the caption kept for screen readers alone. Above it the full docked
 * tray with its caption is what fits. Same number as HomeNav's
 * MENU_BREAKPOINT_PX, deliberately: it is the width at which the pill's whole
 * layout changes, and the chip has to change with it, not on a width of its
 * own.
 *
 * It USED to be a floor - below 1080 the tray refused to dock at all and the
 * live count simply stopped existing once a phone visitor scrolled past the
 * hero. Owner round 4 asked for the phone dock; this is the width where the
 * two shapes meet, not where docking stops.
 */
export const DOCK_COMPACT_MAX_WIDTH = 1080;

/**
 * Which shape the docked tray takes at a given viewport width.
 *
 * A pure function of one number so the breakpoint is testable without a
 * layout: the component only ever asks this.
 *
 * @param {number} width viewport width in CSS px.
 * @returns {boolean} true for the phone chip, false for the full tray.
 */
export function isCompactDock(width) {
  return width <= DOCK_COMPACT_MAX_WIDTH;
}
/** Dock when the tray's home reaches the navbar's bottom edge, plus this. */
const DOCK_ENTER_OFFSET = 6;
/** Undock only once it is this far back below - hysteresis, or it flickers. */
const DOCK_EXIT_OFFSET = 40;
/** How long to keep re-placing the docked tray while the navbar animates. */
const FOLLOW_MS = 900;
const FLIP_MS = 600;
const EASE = "cubic-bezier(.22,1,.36,1)";

const navBottom = () => {
  const header = document.querySelector("header[role='banner']");
  return header ? header.getBoundingClientRect().bottom : 0;
};

/**
 * Should the tray be docked, given where its home is and where it is now?
 *
 * Split out and exported because the hysteresis is the whole point and it is a
 * pure function of three numbers: a single threshold makes the tray flicker
 * between states on every sub-pixel scroll near the boundary.
 *
 * @param {boolean} docked current state.
 * @param {number} homeTop the tray's resting top, in viewport coordinates.
 * @param {number} navEdge the navbar's bottom edge.
 * @returns {boolean} the state it should be in.
 */
export function shouldDock(docked, homeTop, navEdge) {
  if (!docked) return homeTop <= navEdge + DOCK_ENTER_OFFSET;
  return !(homeTop > navEdge + DOCK_EXIT_OFFSET);
}

const ScanIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-4-4" />
    <path d="M8 11h6" />
  </svg>
);

/**
 * @param {object} props
 * @param {string} props.label the caption.
 * @param {boolean} props.motionOff no ping, no FLIP.
 * @param {boolean} props.staticCount render a frozen 0 (prerender).
 * @param {"rtl"|"ltr"} [props.dir] the page's text direction. The docked tray
 *   is portalled to document.body, OUTSIDE the layout that carries `dir="rtl"`,
 *   so it has to carry its own or the Hebrew caption renders LTR in the navbar.
 */
function ScannedTray({ label, motionOff = false, staticCount = false, dir }) {
  const [count, setCount] = useState(0);
  const [docked, setDocked] = useState(false);
  // Seeded from the current width so the FIRST docked render is already the
  // right shape: `sizeSlot` measures the element in a layout effect, and a
  // full-width tray measured for a phone pill would size the slot to ~280px
  // and blow the row out for one frame.
  const [compact, setCompact] = useState(() =>
    typeof window === "undefined" ? false : isCompactDock(window.innerWidth)
  );
  const compactRef = useRef(compact);
  const trayRef = useRef(null);
  const homeRef = useRef(null);
  const flipFromRef = useRef(null);
  const startedAtRef = useRef(null);
  // Stamped at the FIRST RENDER, not inside the counting effect, because
  // "since you arrived" has to mean arrival - not "since the counter was last
  // allowed to run". A visitor who lands with motion off, reads for a minute
  // and then turns it on gets the minute they were actually here.
  if (startedAtRef.current === null) startedAtRef.current = Date.now();

  // ------------------------------------------------------------------ counting
  useEffect(() => {
    if (staticCount) return undefined;
    // Motion off: no interval, and the last value stays on screen. Written as a
    // bail rather than a `start()` guard so there is provably no timer left -
    // "stopped" has to mean stopped, not "running and discarding".
    if (motionOff) return undefined;

    let interval = null;
    const tick = () => {
      const elapsed = Date.now() - startedAtRef.current;
      setCount(Math.floor(elapsed / TICK_MS));
    };
    const start = () => {
      if (interval) return;
      tick();
      interval = setInterval(tick, TICK_MS);
    };
    const stop = () => {
      clearInterval(interval);
      interval = null;
    };

    const onVisibility = () => {
      // Nothing to show and nothing to burn a timer for while hidden; the
      // elapsed-time derivation is what makes coming back correct.
      if (document.visibilityState === "hidden") stop();
      else start();
    };

    if (document.visibilityState !== "hidden") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [staticCount, motionOff]);

  // The ping. Restarting a CSS animation needs the class removed, a reflow
  // forced, and the class re-added - React cannot express that across two
  // renders, so it is done imperatively on the same element.
  useEffect(() => {
    if (motionOff || count === 0) return;
    const tray = trayRef.current;
    if (!tray) return;
    tray.classList.remove("hl-tray-tick");
    void tray.offsetWidth;
    tray.classList.add("hl-tray-tick");
  }, [count, motionOff]);

  // --------------------------------------------------------------------- dock
  /** Put the docked tray on top of the navbar's slot. */
  const placeDocked = useCallback(() => {
    const tray = trayRef.current;
    const slot = document.getElementById(DOCK_SLOT_ID);
    if (!tray || !slot) return;
    const rect = slot.getBoundingClientRect();
    tray.style.left = `${rect.left}px`;
    tray.style.top = `${rect.top + rect.height / 2 - tray.offsetHeight / 2}px`;
  }, []);

  /** Grow or collapse the navbar's slot so the toolbar makes room. */
  const sizeSlot = useCallback((on, asChip) => {
    const slot = document.getElementById(DOCK_SLOT_ID);
    const tray = trayRef.current;
    if (!slot) return;
    if (on && tray) {
      // The LAYOUT width (offsetWidth), not the rect: this also runs while the
      // FLIP's scale transform is in flight, and a transformed rect would size
      // the slot for the tray's old shape. jsdom has no layout, so it falls
      // back to the rect there.
      const width = tray.offsetWidth || Math.round(tray.getBoundingClientRect().width);
      const next = `${width}px`;
      // Only a real change is written: the nav observes this attribute.
      if (slot.style.width !== next) slot.style.width = next;
      // -8px cancels the toolbar group's own gap; 12px is the prototype's
      // spacing from the wordmark. Net +4px, and exactly 0 when collapsed.
      //
      // The phone chip gives that +4 back: every px of margin here comes
      // straight off the phone row's budget (homeNav.css's phone-pill block),
      // and 0 still leaves the row's own gap on each side of the chip, which
      // is the air it needs.
      slot.style.marginLeft = asChip ? "0px" : "4px";
    } else {
      slot.style.width = "0px";
      slot.style.marginLeft = "-8px";
    }
  }, []);

  /**
   * The docked flag as a REF, written at the same instant as the state.
   *
   * The scroll handler cannot read the state variable: a scroll event that fires
   * between `setDocked(true)` and React's commit runs the previous render's
   * closure, which still says "undocked" while the DOM is already docked - and
   * then `measureHome` records the NAVBAR's position as the tray's home, after
   * which it can never undock. Measured, not theorised: it pinned the tray to
   * the navbar for the rest of the visit. One ref, written synchronously, is the
   * fix; it also lets the listeners be subscribed once instead of re-subscribed
   * on every dock transition.
   */
  const dockedRef = useRef(false);
  const setDockedNow = useCallback((next) => {
    dockedRef.current = next;
    setDocked(next);
  }, []);

  useEffect(() => {
    if (staticCount) return undefined;

    let frame = 0;
    const measureHome = () => {
      const tray = trayRef.current;
      // Only measurable while it is in its home - docked, its rect IS the
      // navbar's, and recording that is the bug described above.
      if (!tray || dockedRef.current) return;
      homeRef.current = tray.getBoundingClientRect().top + window.scrollY;
    };

    const evaluate = () => {
      frame = 0;
      const tray = trayRef.current;
      if (!tray) return;
      const isDocked = dockedRef.current;
      // NO SLOT, NO DOCK. The width floor used to make this unreachable by
      // accident; now that the tray docks at every width, the slot's existence
      // is the real precondition and it is checked directly. Without it
      // `navBottom()` is 0 and an unmeasured home is 0, so `shouldDock` says
      // yes and the tray goes `position: fixed` at the top-left corner with
      // nothing to sit in - which is what a page rendering the tray without
      // HomeNav would have got.
      if (!document.getElementById(DOCK_SLOT_ID)) {
        if (isDocked) {
          flipFromRef.current = tray.getBoundingClientRect();
          setDockedNow(false);
        }
        return;
      }
      // The chip/tray shape follows the viewport width and is re-read here
      // rather than from a resize listener of its own: `evaluate` already runs
      // on every scroll frame AND on resize, and the shape has to be right
      // BEFORE `sizeSlot` measures the element it is about to make room for.
      // Through the ref, so the common case (every scroll frame, width
      // unchanged) does not reach React at all.
      const nextCompact = isCompactDock(window.innerWidth);
      if (nextCompact !== compactRef.current) {
        compactRef.current = nextCompact;
        setCompact(nextCompact);
      }
      // Re-measured on every undocked evaluation, not just once: `top + scrollY`
      // is invariant under scrolling, so this costs one rect per scroll frame,
      // and it is what makes the home self-heal after a late layout shift - the
      // webfont swap re-fits the headline and moves the whole stage, and a home
      // measured before that would dock at the wrong scroll position for the
      // rest of the visit.
      if (!isDocked) measureHome();
      if (homeRef.current === null) return;
      const homeTop = homeRef.current - window.scrollY;
      const next = shouldDock(isDocked, homeTop, navBottom());
      if (next !== isDocked) {
        flipFromRef.current = tray.getBoundingClientRect();
        setDockedNow(next);
      } else if (isDocked) {
        placeDocked();
      }
    };

    const onScroll = () => {
      // rAF-throttled directly rather than through rafCoordinator: this must
      // keep working with motion off (the dock is layout, not decoration) and
      // it runs only while the page is being scrolled.
      if (frame) return;
      frame = requestAnimationFrame(evaluate);
    };
    const onResize = () => {
      measureHome();
      onScroll();
    };

    measureHome();
    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    // The webfont swap re-lays out the hero after first paint, which moves the
    // tray's home. One more measurement then, rather than trusting the first.
    if (typeof document !== "undefined" && document.fonts?.ready?.then) {
      document.fonts.ready.then(measureHome).catch(() => {
        /* the per-scroll re-measure is the fallback */
      });
    }
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (frame) cancelAnimationFrame(frame);
    };
    // Subscribed ONCE. The handlers read dockedRef, so a dock transition needs
    // no re-subscription - and cannot open the window in which a stale closure
    // mismeasures the home.
  }, [placeDocked, setDockedNow, staticCount]);

  // The FLIP. `first` was measured before the state change (the element is
  // remounted by the portal switch, so it has to be); `last` is measured here,
  // after the new position is applied but before the browser paints.
  useLayoutEffect(() => {
    const tray = trayRef.current;
    const first = flipFromRef.current;
    flipFromRef.current = null;

    sizeSlot(docked, compact);
    if (docked) placeDocked();
    else if (tray) {
      tray.style.left = "";
      tray.style.top = "";
    }
    if (!tray || !first) return undefined;

    if (!motionOff && typeof tray.animate === "function") {
      const last = tray.getBoundingClientRect();
      const dx = first.left - last.left;
      const dy = first.top - last.top;
      const scale = last.width ? first.width / last.width : 1;
      tray.animate(
        [
          { transform: `translate(${dx}px,${dy}px) scale(${scale})`, transformOrigin: "left top" },
          { transform: "none", transformOrigin: "left top" },
        ],
        { duration: FLIP_MS, easing: EASE }
      );
    }

    if (!docked) return undefined;
    // The navbar's own width transition runs for half a second after the dock,
    // so the slot keeps moving under the tray. Follow it until it settles.
    // Motion off zeroes that transition (homepage.css), so the pill jumps to
    // its docked width in one step - the ResizeObserver below re-places the
    // tray for that step, and a frame loop here would be a rAF running under
    // the switch that forbids it.
    if (motionOff) return undefined;
    let frame = 0;
    const startedAt = Date.now();
    const follow = () => {
      placeDocked();
      if (Date.now() - startedAt < FOLLOW_MS) frame = requestAnimationFrame(follow);
    };
    frame = requestAnimationFrame(follow);
    return () => cancelAnimationFrame(frame);
    // `compact` is a dependency because a rotation that crosses the breakpoint
    // while docked changes the chip's width, and the slot has to be re-sized
    // and the chip re-placed for it.
  }, [compact, docked, motionOff, placeDocked, sizeSlot]);

  // While docked, re-place the tray whenever the PILL changes size (the
  // has-dock / compact widths land a render after the dock - see the header)
  // and re-size the slot whenever the TRAY does (the count gains a glyph, the
  // chip/tray shape swaps). A ResizeObserver delivers after layout and before
  // paint, so the tray never shows a frame off its slot, and it is not a loop:
  // re-sizing the slot changes neither observed box, so it settles at once.
  // Where it does not exist (jsdom, the prerender) the dock-time placement and
  // the scroll handler are what they always were.
  useEffect(() => {
    if (!docked || typeof window === "undefined" || typeof window.ResizeObserver !== "function") {
      return undefined;
    }
    const tray = trayRef.current;
    const slot = document.getElementById(DOCK_SLOT_ID);
    if (!tray || !slot) return undefined;
    const observer = new window.ResizeObserver((entries) => {
      if (entries.some((entry) => entry.target === tray)) sizeSlot(true, compactRef.current);
      placeDocked();
    });
    observer.observe(tray);
    if (slot.parentElement) observer.observe(slot.parentElement);
    return () => observer.disconnect();
  }, [docked, placeDocked, sizeSlot]);

  // Release the navbar slot if the page goes away while docked.
  useEffect(() => () => sizeSlot(false, false), [sizeSlot]);

  // The chip is the DOCKED shape on a phone, so `hl-tray-chip` is only ever
  // added alongside `hl-tray-docked` - the tray at home is the full card at
  // every width.
  const asChip = docked && compact;
  const tray = (
    <div
      ref={trayRef}
      className={
        docked
          ? `hl-root hl-tray hl-tray-docked${asChip ? " hl-tray-chip" : ""}`
          : "hl-tray"
      }
      data-docked={docked ? "true" : "false"}
      data-chip={asChip ? "true" : "false"}
      dir={dir}
    >
      <span className="hl-tray-icon" aria-hidden="true">
        <ScanIcon />
        <i />
      </span>
      {/* The caption is CLIPPED on the chip, never removed: the number on its
          own reads as "1,247" to a screen reader, which is not a claim about
          anything. `.hl-tray-chip > span` in homepage.css is the standard
          visually-hidden clip, so the sentence stays in the accessibility
          tree while the pill only pays for the icon and the digits. */}
      <span>{label}</span>
      <b>{count.toLocaleString("en-US")}</b>
    </div>
  );

  // Portalled only while docked: in its home it is a normal child of the stage,
  // and a portal there would break the stage's own layout.
  if (!docked) return tray;
  return createPortal(tray, document.body);
}

// Primitive props only, so the hero's own re-renders (a new lead, the HOT
// stamp, a burst ring) never reach the tray.
export default React.memo(ScannedTray);
